<template>
    <div>
        <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
            {{ $t('Verfügbare Artikel') }}
        </h3>
        <kgr-club-sales-list-items
            :club-sales-list-items="availableFees"
            :categories="clubSalesListConfig.clubFeeCategories"
            :with-filter="clubSalesListConfig.clubFeeCategories.length > 1"
            has-action
            with-search
            with-pagination
        >
            <template #action="{ item }">
                <kgr-button
                    :value="$t('Hinzufügen')"
                    type="blue"
                    @click="addItem(item)"
                />
            </template>
        </kgr-club-sales-list-items>

        <div class="kgr-article-sales-checkout">
            <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                {{ $t('Warenkorb') }}
            </h3>
            <kgr-club-sales-list-items
                :club-sales-list-items="selectedItems"
                has-action
                with-total-price
                is-aggregated
            >
                <template #action="{ item }">
                    <kgr-button
                        :value="$t('Entfernen')"
                        type="blue"
                        @click="removeItem(item)"
                    />
                </template>
            </kgr-club-sales-list-items>
            <kgr-button
                :value="$t('Bezahlen')"
                type="blue"
                :disabled="selectedItems.length === 0"
                @click="onCreateInvoice"
            />
            <kgr-button
                v-if="selectedItems.length > 0"
                :value="$t('Zurücksetzen')"
                type="grey"
                @click="resetCheckout"
            />
        </div>
        <kgr-member-article-sales-invoice-dialog
            :show="showCheckoutDialog"
            :fees="selectedItems"
            :pending="pending"
            :pending-invoicing="pendingInvoicingStatus"
            :club-sales-list-config="clubSalesListConfig"
            :has-sepa-debit="hasSepaDebit"
            :is-credit-payment-available="isCreditPaymentAvailable"
            :remaining-member-credit="remainingMemberCredit"
            @onClose="onClose"
            @onConfirm="onCheckout($event)"
        />
    </div>
</template>

<script>
import ClubMemberCreditRepository from '../../../js/repository/ClubMemberCreditRepository';
import ClubSalesListConfig from '../../../js/dto/ClubSalesListConfig';
import ClubSalesListInvoicingRepository from '../../../js/repository/ClubSalesListInvoicingRepository';
import ClubSalesListItemRepository from '../../../js/repository/ClubSalesListItemRepository';
import ClubSalesListItems from '../../club/sales-list/ClubSalesListItems';
import Error from '../../../js/model/Error';
import KgrButton from '../../common/form/button/KgrButton';
import MemberArticleSalesInvoiceDialog from '../../members/article-sales/MemberArticleSalesInvoiceDialog';
import Money from '../../../js/dto/Money';

export default {
    components: {
        'kgr-button': KgrButton,
        'kgr-club-sales-list-items': ClubSalesListItems,
        'kgr-member-article-sales-invoice-dialog': MemberArticleSalesInvoiceDialog,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        memberId: {
            type: Number,
            required: true,
        },
        clubSalesListConfig: {
            type: ClubSalesListConfig,
            required: true,
        },
        availableFees: {
            type: Array,
            required: true,
        },
        hasSepaDebit: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            pending: false,
            pendingInvoicingStatus: false,
            showCheckoutDialog: false,
            selectedItems: [],
            remainingMemberCredit: Money.fromNumber(0),
            isInvoicingRunning: true,
        };
    },
    computed: {
        isCreditPaymentAvailable() {
            return this.hasSufficientCredits && !this.isInvoicingRunning;
        },
        hasSufficientCredits() {
            let totalPrice = 0;
            this.selectedItems.forEach((item) => {
                totalPrice += (item.pricePerTime.gross.numerator / item.pricePerTime.gross.denominator);
            });
            return totalPrice <= this.remainingMemberCredit.toNumber();
        },
    },
    mounted() {
        this.loadInvoicingStatus();
    },
    methods: {
        addItem(item) {
            this.selectedItems.push(item);
        },
        removeItem(item) {
            const index = this.selectedItems.findIndex((i) => i.id === item.id);
            this.selectedItems.splice(index, 1);
        },
        onCreateInvoice() {
            this.loadInvoicingStatus();
            this.showCheckoutDialog = true;
        },
        resetCheckout() {
            this.selectedItems = [];
        },
        onClose() {
            this.showCheckoutDialog = false;
        },
        loadMemberCredits() {
            ClubMemberCreditRepository.getRemainingBalanceForSalesList(this.memberId)
                .then((remainingCredit) => {
                    this.error = new Error();
                    this.remainingMemberCredit = remainingCredit;
                })
                .catch((error) => {
                    this.error = error;
                });
        },
        loadInvoicingStatus() {
            ClubSalesListInvoicingRepository.getInvoicingStatus(this.memberId)
                .then((isInvoicingRunning) => {
                    this.error = new Error();
                    this.isInvoicingRunning = isInvoicingRunning;
                    if (isInvoicingRunning) {
                        setTimeout(() => {
                            this.pendingInvoicingStatus = true;
                            this.loadInvoicingStatus();
                        }, 1000);
                    } else {
                        this.pendingInvoicingStatus = false;
                        this.loadMemberCredits();
                    }
                })
                .catch((error) => {
                    this.error = error;
                    this.pendingInvoicingStatus = false;
                });
        },
        onCheckout(data) {
            this.pending = true;
            ClubSalesListItemRepository.createInvoice(this.memberId, data)
                .then(() => {
                    this.error = new Error();
                    this.$notify.success('Einkauf erfolgreich');
                    this.pending = false;
                    this.showCheckoutDialog = false;
                    this.selectedItems = [];
                    this.loadInvoicingStatus();
                })
                .catch((error) => {
                    this.error = error;
                    this.$notify.error('Es ist etwas schief gelaufen');
                    this.pending = false;
                    this.showCheckoutDialog = false;
                    this.loadInvoicingStatus();
                });
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
.kgr-article-sales-checkout {
    margin-top: 60px;
}
</style>
